import api from './api';

export type JobStatus = 'bozza' | 'aperta' | 'chiusa';
export type ContractType =
  | 'tempo_indeterminato' | 'tempo_determinato' | 'apprendistato'
  | 'stage_tirocinio' | 'partita_iva' | 'somministrazione';
export type Seniority = 'junior' | 'middle' | 'senior' | 'manager';
export type Department =
  | 'commerciale' | 'logistica' | 'assistenza_tecnica' | 'customer_service'
  | 'amministrazione' | 'marketing' | 'it' | 'acquisti';
export type SalaryPeriod = 'ora' | 'mese' | 'anno';
export type RemotePolicy = 'in_sede' | 'ibrido' | 'remoto';

export const CONTRACT_LABELS: Record<ContractType, string> = {
  tempo_indeterminato: 'Tempo indeterminato',
  tempo_determinato: 'Tempo determinato',
  apprendistato: 'Apprendistato',
  stage_tirocinio: 'Stage / Tirocinio',
  partita_iva: 'Partita IVA',
  somministrazione: 'Somministrazione',
};

export const SENIORITY_LABELS: Record<Seniority, string> = {
  junior: 'Junior',
  middle: 'Middle',
  senior: 'Senior',
  manager: 'Manager',
};

export const DEPARTMENT_LABELS: Record<Department, string> = {
  commerciale: 'Commerciale',
  logistica: 'Logistica',
  assistenza_tecnica: 'Assistenza tecnica',
  customer_service: 'Customer service',
  amministrazione: 'Amministrazione',
  marketing: 'Marketing',
  it: 'IT',
  acquisti: 'Acquisti',
};

export const REMOTE_LABELS: Record<RemotePolicy, string> = {
  in_sede: 'In sede',
  ibrido: 'Ibrido',
  remoto: 'Remoto',
};

export const SALARY_PERIOD_LABELS: Record<SalaryPeriod, string> = {
  ora: 'ora',
  mese: 'mese',
  anno: 'anno',
};

export const STATUS_LABELS: Record<JobStatus, string> = {
  bozza: 'Bozza',
  aperta: 'Aperta',
  chiusa: 'Chiusa',
};

export interface Retribuzione {
  min: number;
  max?: number | null;
  valuta: string;
  periodo: SalaryPeriod;
}

export interface JobPositionCard {
  id: string;
  slug: string;
  titolo: string;
  sede: string;
  sedeRemoto: RemotePolicy;
  tipoContratto: ContractType;
  area: Department;
  seniority: Seniority;
  dataPubblicazione?: string | null;
}

export interface JobPositionDetail extends JobPositionCard {
  stato: JobStatus;
  riferimento?: string | null;
  descrizione: string;
  requisiti: string[];
  requisitiPreferenziali: string[];
  offriamo: string[];
  retribuzione?: Retribuzione | null;
  dataChiusura?: string | null;
  dataScadenzaCandidature?: string | null;
  emailCandidature: string;
  posizioniDisponibili: number;
}

export interface JobHistoryEntry {
  at: string;
  byNome: string;
  azione: string;
  statoPrecedente?: string | null;
  statoNuovo?: string | null;
}

export interface JobPositionAdminDetail extends JobPositionDetail {
  clickCandidature: number;
  deletedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  history: JobHistoryEntry[];
}

export interface JobPositionAdminRow {
  id: string;
  slug: string;
  titolo: string;
  stato: JobStatus;
  area: Department;
  dataPubblicazione?: string | null;
  dataChiusura?: string | null;
  clickCandidature: number;
  deletedAt?: string | null;
  scaduta: boolean;
}

export interface JobPositionPayload {
  titolo: string;
  sede: string;
  tipoContratto: ContractType;
  area: Department;
  seniority: Seniority;
  descrizione: string;
  requisiti: string[];
  slug?: string;
  riferimento?: string | null;
  sedeRemoto: RemotePolicy;
  requisitiPreferenziali: string[];
  offriamo: string[];
  retribuzione?: Retribuzione | null;
  dataPubblicazione?: string | null;
  dataChiusura?: string | null;
  dataScadenzaCandidature?: string | null;
  emailCandidature?: string | null;
  posizioniDisponibili: number;
}

export const careersService = {
  async listPublic(): Promise<{ items: JobPositionCard[]; total: number; emailCandidature: string }> {
    const { data } = await api.get('/api/carriere');
    return data;
  },
  async getPublic(slug: string): Promise<JobPositionDetail> {
    const { data } = await api.get(`/api/carriere/${slug}`);
    return data;
  },
  async trackClick(slug: string): Promise<void> {
    try { await api.post(`/api/carriere/${slug}/click-candidatura`); } catch { /* non bloccante */ }
  },

  async adminList(params: {
    page?: number; pageSize?: number; stato?: JobStatus | ''; q?: string;
    pubblicataDa?: string; pubblicataA?: string; chiusaDa?: string; chiusaA?: string;
    includeDeleted?: boolean;
  }): Promise<{ items: JobPositionAdminRow[]; total: number; page: number; pageSize: number; pages: number }> {
    const qs = new URLSearchParams();
    qs.set('page', String(params.page ?? 1));
    qs.set('page_size', String(params.pageSize ?? 20));
    if (params.stato) qs.set('stato', params.stato);
    if (params.q) qs.set('q', params.q);
    if (params.pubblicataDa) qs.set('pubblicata_da', params.pubblicataDa);
    if (params.pubblicataA) qs.set('pubblicata_a', params.pubblicataA);
    if (params.chiusaDa) qs.set('chiusa_da', params.chiusaDa);
    if (params.chiusaA) qs.set('chiusa_a', params.chiusaA);
    if (params.includeDeleted) qs.set('include_deleted', 'true');
    const { data } = await api.get(`/api/admin/carriere?${qs}`);
    return data;
  },
  async adminGet(id: string): Promise<JobPositionAdminDetail> {
    const { data } = await api.get(`/api/admin/carriere/${id}`);
    return data;
  },
  async adminCreate(payload: JobPositionPayload): Promise<JobPositionAdminDetail> {
    const { data } = await api.post('/api/admin/carriere', payload);
    return data;
  },
  async adminUpdate(id: string, payload: JobPositionPayload, silentHistory = false): Promise<JobPositionAdminDetail> {
    const { data } = await api.put(`/api/admin/carriere/${id}${silentHistory ? '?silent_history=true' : ''}`, payload);
    return data;
  },
  async adminSetStato(id: string, stato: JobStatus): Promise<JobPositionAdminDetail> {
    const { data } = await api.post(`/api/admin/carriere/${id}/stato`, { stato });
    return data;
  },
  async adminDelete(id: string): Promise<void> {
    await api.delete(`/api/admin/carriere/${id}`);
  },
  async adminRestore(id: string): Promise<void> {
    await api.post(`/api/admin/carriere/${id}/ripristina`);
  },
};

export function formatRetribuzione(r?: Retribuzione | null): string | null {
  if (!r) return null;
  const fmt = (v: number) => v.toLocaleString('it-IT');
  const range = r.max && r.max > r.min ? `${fmt(r.min)}–${fmt(r.max)}` : fmt(r.min);
  return `${range} € / ${SALARY_PERIOD_LABELS[r.periodo]}`;
}
